"use client";

import * as React from "react";
import {
  Text,
  SectionCard,
  CardTitle,
  CardDescription,
  Button,
  Input,
  Textarea,
  Badge,
  Switch,
  Label,
  FormField,
  FormFieldGroup,
  FormSection,
  FormActions,
  FormErrorSummary,
  useFormErrors,
  type FormError,
  Checkbox,
  CheckboxGroup,
  RadioGroup,
  RadioGroupItem,
  RadioCard,
  Section,
} from "@/src/components";
import { SelectBlock } from "@/src/components/input/select-advanced";

import { Mail, CreditCard, User } from "lucide-react";

export default function DocsPatternsForms() {
  const { errors, setErrors, clearErrors, getError } = useFormErrors();
  const [formData, setFormData] = React.useState({
    name: "",
    email: "",
    plan: "",
    notifications: false,
    interests: [] as string[],
  });

  const handleDemoValidation = () => {
    const newErrors: FormError[] = [];
    if (!formData.name) newErrors.push({ field: "name", message: "Name is required" });
    if (!formData.email) newErrors.push({ field: "email", message: "Email is required" });
    if (formData.email && !formData.email.includes("@")) {
      newErrors.push({ field: "email", message: "Please enter a valid email" });
    }
    setErrors(newErrors);
  };

  return (
    <>
      {/* Header */}
      <Section>
        <div className="space-y-4">
          <Text as="h1" className="text-h2 font-bold">
            Form Patterns
          </Text>
          <Text className="text-lg text-muted-foreground max-w-2xl">
            Standardized patterns for building accessible, user-friendly forms
            with consistent validation and layout.
          </Text>
        </div>
      </Section>

      {/* Components Overview */}
      <Section>
        <SectionCard
        header={<CardTitle>Form Components</CardTitle>}
        contentClassName="p-6"
      >
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <div className="space-y-1">
            <Badge variant="secondary">FormField</Badge>
            <Text className="text-xs text-muted-foreground">
              Input wrapper with label, hint, error
            </Text>
          </div>
          <div className="space-y-1">
            <Badge variant="secondary">FormSection</Badge>
            <Text className="text-xs text-muted-foreground">
              Groups related fields together
            </Text>
          </div>
          <div className="space-y-1">
            <Badge variant="secondary">FormActions</Badge>
            <Text className="text-xs text-muted-foreground">
              Submit/cancel button container
            </Text>
          </div>
          <div className="space-y-1">
            <Badge variant="secondary">FormErrorSummary</Badge>
            <Text className="text-xs text-muted-foreground">
              Top-of-form error list
            </Text>
          </div>
          <div className="space-y-1">
            <Badge variant="secondary">Checkbox</Badge>
            <Text className="text-xs text-muted-foreground">
              Checkbox with label/description
            </Text>
          </div>
          <div className="space-y-1">
            <Badge variant="secondary">RadioGroup</Badge>
            <Text className="text-xs text-muted-foreground">
              Radio buttons with card variant
            </Text>
          </div>
        </div>
      </SectionCard>
      </Section>

      {/* FormField */}
      <Section>
        <SectionCard
        header={
          <>
            <CardTitle>FormField</CardTitle>
            <CardDescription>Wrapper for form inputs with automatic accessibility attributes</CardDescription>
          </>
        }
        contentClassName="p-6 space-y-6"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField
            label="Full Name"
            htmlFor="demo-name"
            required
            hint="Enter your first and last name"
          >
            <Input placeholder="John Doe" />
          </FormField>

          <FormField
            label="Email"
            htmlFor="demo-email"
            required
            error="Please enter a valid email address"
          >
            <Input type="email" placeholder="you@example.com" />
          </FormField>
        </div>

        <div className="bg-muted/50 rounded-lg p-4 font-mono text-sm overflow-x-auto">
          <code>{`<FormField
  label="Email"
  htmlFor="email"
  required
  error={errors.email}
  hint="We'll never share your email"
>
  <Input type="email" />
</FormField>`}</code>
        </div>
      </SectionCard>
      </Section>

      {/* FormFieldGroup */}
      <Section>
        <SectionCard
        header={
          <>
            <CardTitle>FormFieldGroup</CardTitle>
            <CardDescription>Groups related fields with a legend</CardDescription>
          </>
        }
        contentClassName="p-6"
      >
        <FormFieldGroup
          legend="Contact Information"
          description="How can we reach you?"
        >
          <FormField label="Phone" htmlFor="phone">
            <Input type="tel" placeholder="+1 (555) 000-0000" />
          </FormField>
          <FormField label="Website" htmlFor="website">
            <Input type="url" placeholder="https://example.com" />
          </FormField>
        </FormFieldGroup>
      </SectionCard>
      </Section>

      {/* FormSection */}
      <Section>
        <SectionCard
        header={
          <>
            <CardTitle>FormSection</CardTitle>
            <CardDescription>Organizes forms into logical sections</CardDescription>
          </>
        }
        contentClassName="p-6"
      >
        <form className="border rounded-lg">
          <FormSection
            title="Personal Information"
            description="Tell us about yourself"
            layout="two-column"
          >
            <FormField label="First Name" htmlFor="first-name" required>
              <Input placeholder="John" />
            </FormField>
            <FormField label="Last Name" htmlFor="last-name" required>
              <Input placeholder="Doe" />
            </FormField>
          </FormSection>

          <FormSection
            title="Account Settings"
            description="Configure your account preferences"
            showDivider
          >
            <FormField label="Username" htmlFor="username" required>
              <Input placeholder="johndoe" />
            </FormField>
            <FormField label="Bio" htmlFor="bio" hint="Brief description for your profile">
              <Textarea placeholder="Tell us about yourself..." />
            </FormField>
          </FormSection>

          <FormActions>
            <Button variant="outline">Cancel</Button>
            <Button type="submit">Save Changes</Button>
          </FormActions>
        </form>
      </SectionCard>
      </Section>

      {/* Checkbox */}
      <Section>
        <SectionCard
        header={
          <>
            <CardTitle>Checkbox</CardTitle>
            <CardDescription>Checkbox with integrated label and description</CardDescription>
          </>
        }
        contentClassName="p-6 space-y-6"
      >
        <div className="space-y-4">
          <Checkbox label="Accept terms and conditions" />
          <Checkbox
            label="Subscribe to newsletter"
            description="Get the latest updates and news delivered to your inbox"
          />
          <Checkbox
            label="Enable notifications"
            description="Receive push notifications for important updates"
            error="You must enable notifications to continue"
          />
          <Checkbox label="Disabled option" disabled />
          <Checkbox label="Indeterminate state" indeterminate />
        </div>

        <CheckboxGroup
          legend="Notification Preferences"
          description="Choose how you want to be notified"
        >
          <Checkbox label="Email notifications" />
          <Checkbox label="SMS notifications" />
          <Checkbox label="Push notifications" />
        </CheckboxGroup>
      </SectionCard>
      </Section>

      {/* RadioGroup */}
      <Section>
        <SectionCard
        header={
          <>
            <CardTitle>RadioGroup</CardTitle>
            <CardDescription>Radio buttons for single selection</CardDescription>
          </>
        }
        contentClassName="p-6 space-y-8"
      >
        <RadioGroup
          legend="Select your plan"
          description="Choose the plan that works best for you"
          defaultValue="basic"
        >
          <RadioGroupItem value="basic" label="Basic" description="$9/month" />
          <RadioGroupItem value="pro" label="Pro" description="$19/month" />
          <RadioGroupItem value="enterprise" label="Enterprise" description="Custom pricing" />
        </RadioGroup>

        <div>
          <Text className="font-medium mb-4">Card Variant</Text>
          <RadioGroup defaultValue="card" className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <RadioCard
              value="starter"
              title="Starter"
              description="Perfect for small projects"
              icon={<User className="h-5 w-5" />}
            />
            <RadioCard
              value="business"
              title="Business"
              description="For growing teams"
              icon={<CreditCard className="h-5 w-5" />}
            />
            <RadioCard
              value="enterprise"
              title="Enterprise"
              description="Custom solutions"
              icon={<Mail className="h-5 w-5" />}
            />
          </RadioGroup>
        </div>
      </SectionCard>
      </Section>

      {/* FormErrorSummary */}
      <Section>
        <SectionCard
        header={
          <>
            <CardTitle>FormErrorSummary</CardTitle>
            <CardDescription>Display all form errors at the top</CardDescription>
          </>
        }
        contentClassName="p-6 space-y-6"
      >
        <div className="flex gap-4 mb-4">
          <Button variant="outline" onClick={handleDemoValidation}>
            Trigger Validation
          </Button>
          <Button variant="text" onClick={clearErrors}>
            Clear Errors
          </Button>
        </div>

        <FormErrorSummary errors={errors} />

        <form className="space-y-4">
          <FormField
            label="Name"
            htmlFor="name"
            required
            error={getError("name")}
          >
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Your name"
            />
          </FormField>

          <FormField
            label="Email"
            htmlFor="email"
            required
            error={getError("email")}
          >
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="you@example.com"
            />
          </FormField>
        </form>
      </SectionCard>
      </Section>

      {/* Complete Form Example */}
      <Section>
        <SectionCard
        header={
          <>
            <CardTitle>Complete Form Example</CardTitle>
            <CardDescription>All patterns combined</CardDescription>
          </>
        }
        contentClassName="p-6"
      >
        <form className="border rounded-lg">
          <FormSection title="Account Details" layout="two-column">
            <FormField label="First Name" htmlFor="ex-first" required>
              <Input placeholder="John" />
            </FormField>
            <FormField label="Last Name" htmlFor="ex-last" required>
              <Input placeholder="Doe" />
            </FormField>
            <FormField
              label="Email"
              htmlFor="ex-email"
              required
              hint="We'll send your confirmation here"
              className="md:col-span-2"
            >
              <Input type="email" placeholder="you@example.com" />
            </FormField>
          </FormSection>

          <FormSection title="Preferences" showDivider>
            <RadioGroup
              legend="Subscription Plan"
              defaultValue="monthly"
              orientation="horizontal"
              hideLegend
            >
              <RadioGroupItem value="monthly" label="Monthly" />
              <RadioGroupItem value="yearly" label="Yearly (Save 20%)" />
            </RadioGroup>

            <CheckboxGroup legend="Features" hideLegend orientation="horizontal">
              <Checkbox label="Email updates" defaultChecked />
              <Checkbox label="SMS notifications" />
              <Checkbox label="Marketing emails" />
            </CheckboxGroup>

            <div className="flex items-center gap-3 pt-4">
              <Switch id="terms" />
              <Label htmlFor="terms" className="text-sm">
                I agree to the terms and conditions
              </Label>
            </div>
          </FormSection>

          <FormActions>
            <Button variant="outline" type="button">Cancel</Button>
            <Button type="submit">Create Account</Button>
          </FormActions>
        </form>
      </SectionCard>
      </Section>

      {/* Accessibility */}
      <Section>
        <SectionCard
        header={<CardTitle>Accessibility</CardTitle>}
        contentClassName="p-6 space-y-4"
      >
        <Text className="text-sm text-muted-foreground">
          All form components are built with accessibility in mind:
        </Text>
        <ul className="space-y-2 text-sm">
          <li>• <strong>Labels:</strong> Every input has an associated label via htmlFor/id</li>
          <li>• <strong>Required fields:</strong> Marked with aria-required and visual indicator</li>
          <li>• <strong>Error states:</strong> aria-invalid and aria-describedby for screen readers</li>
          <li>• <strong>Error summary:</strong> Auto-focuses and links to invalid fields</li>
          <li>• <strong>Fieldsets:</strong> Related fields grouped with legend for context</li>
          <li>• <strong>Live regions:</strong> Errors announced with role="alert"</li>
        </ul>
      </SectionCard>
      </Section>

      {/* Usage Pattern */}
      <Section>
        <SectionCard
        header={<CardTitle>Usage Pattern</CardTitle>}
        contentClassName="p-6"
      >
        <div className="bg-muted/50 rounded-lg p-4 font-mono text-sm overflow-x-auto space-y-1">
          <code className="block">{"// With React Hook Form + Zod"}</code>
          <code className="block">{"const { register, handleSubmit, formState: { errors } } = useForm({"}</code>
          <code className="block pl-4">{"resolver: zodResolver(schema)"}</code>
          <code className="block">{"});"}</code>
          <code className="block mt-4">{"<form onSubmit={handleSubmit(onSubmit)}>"}</code>
          <code className="block pl-4">{"<FormErrorSummary errors={Object.entries(errors).map(([k, v]) => ({"}</code>
          <code className="block pl-8">{"field: k, message: v.message"}</code>
          <code className="block pl-4">{"})))} />"}</code>
          <code className="block mt-2 pl-4">{"<FormField label=\"Email\" htmlFor=\"email\" error={errors.email?.message}>"}</code>
          <code className="block pl-8">{"<Input {...register(\"email\")} />"}</code>
          <code className="block pl-4">{"</FormField>"}</code>
          <code className="block mt-2 pl-4">{"<FormActions>"}</code>
          <code className="block pl-8">{"<Button type=\"submit\">Submit</Button>"}</code>
          <code className="block pl-4">{"</FormActions>"}</code>
          <code className="block">{"</form>"}</code>
        </div>
      </SectionCard>
      </Section>
    </>
  );
}
